import React, { useState, useEffect } from 'react';
import { Skeleton } from 'antd';
import { useTheme } from '../../context/ThemeContext';
import { ImagePreviewProps } from './types';
import {
  StyledModal,
  ImageContainer,
  PreviewImage,
  FallbackContainer,
  SkeletonContainer,
} from './styles';

const ImagePreview: React.FC<ImagePreviewProps> = ({
  isOpen,
  onClose,
  imageUrl,
  altText = 'Image Preview',
  fallbackSrc = 'https://via.placeholder.com/400x300',
}) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (isOpen && imageUrl) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [isOpen, imageUrl]);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleModalClose = () => {
    setImageError(false);
    setImageLoading(true);
    onClose();
  };

  return (
    <StyledModal
      open={isOpen}
      onCancel={handleModalClose}
      footer={null}
      width={800}
      centered
      theme={theme}
    >
      <ImageContainer theme={theme}>
        {imageLoading && imageUrl && !imageError ? (
          <SkeletonContainer theme={theme}>
            <Skeleton.Image
              active
              style={{
                width: '100%',
                height: '400px',
                maxHeight: '80vh',
              }}
            />
          </SkeletonContainer>
        ) : imageError || !imageUrl ? (
          <FallbackContainer theme={theme}>
            <PreviewImage src={fallbackSrc} alt={altText} />
          </FallbackContainer>
        ) : (
          <PreviewImage
            src={imageUrl}
            alt={altText}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: imageLoading ? 'none' : 'block' }}
          />
        )}
      </ImageContainer>
    </StyledModal>
  );
};

export default ImagePreview;
